import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

export const useFeeTypes = () => {
  const navigate = useNavigate();
  const [feeTypes, setFeeTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchFeeTypes = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {
          page: params.page || pagination.current,
          pageSize: params.pageSize || pagination.pageSize,
        };

        // Only add filters that have valid values
        if (params.filters) {
          Object.entries(params.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
              queryObj[key] = value;
            }
          });
        }

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.finance.feeTypes}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          const data = response.data || response;
          setFeeTypes(Array.isArray(data) ? data : data.results || []);
          if (data.count) {
            setPagination((prev) => ({ ...prev, total: data.count }));
          }
        } else {
          Utilities.showError(response, "Failed to fetch fee types");
        }
      } catch (error) {
        message.error("An error occurred while fetching fee types");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [navigate, pagination.current, pagination.pageSize],
  );

  const createFeeType = useCallback(
    async (data) => {
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.feeTypes,
          data,
          navigate,
        );

        if (response.ok) {
          message.success("Fee type created successfully");
          fetchFeeTypes({ page: 1 });
          return response.data;
        } else {
          Utilities.showError(response, "Failed to create fee type");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while creating fee type");
        console.error(error);
        return null;
      }
    },
    [navigate, fetchFeeTypes],
  );

  const updateFeeType = useCallback(
    async (id, data) => {
      try {
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          `${domainSettings.urls.finance.feeTypes}/${id}`,
          data,
          navigate,
        );

        if (response.ok) {
          message.success("Fee type updated successfully");
          fetchFeeTypes();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to update fee type");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while updating fee type");
        console.error(error);
        return null;
      }
    },
    [navigate, fetchFeeTypes],
  );

  const deleteFeeType = useCallback(
    async (id) => {
      try {
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          `${domainSettings.urls.finance.feeTypes}/${id}`,
          null,
          navigate,
        );

        if (response.ok) {
          message.success("Fee type deleted successfully");
          fetchFeeTypes();
          return true;
        } else {
          // Check if error is due to fee type being used in fee structures
          const errorMsg =
            response.msg || response.data?.error || "Failed to delete fee type";
          if (
            errorMsg.toLowerCase().includes("cannot delete") ||
            errorMsg.toLowerCase().includes("in use") ||
            errorMsg.toLowerCase().includes("fee structure")
          ) {
            message.error(
              "Cannot delete fee type because it is being used in fee structures",
            );
          } else {
            message.error(errorMsg);
          }
          return false;
        }
      } catch (error) {
        message.error("An error occurred while deleting fee type");
        console.error(error);
        return false;
      }
    },
    [navigate, fetchFeeTypes],
  );

  return {
    feeTypes,
    loading,
    pagination,
    setPagination,
    fetchFeeTypes,
    createFeeType,
    updateFeeType,
    deleteFeeType,
  };
};
